import React, { useState } from "react";
import { TravelData } from "../../components/Data/TravelData";
import {
  Navbar,
  Nav,
  NavDropdown,
  Container,
  Form,
  FormControl,
  Row,
  Col,
  Button,
  Spinner,
} from "react-bootstrap";
import { NavLink } from "react-router-dom";
import { LoadScript } from "@react-google-maps/api";

import AppNavbar from "../../components/common/AppNavbar";

const GOOGLE_API_KEY = "AIzaSyCpAApH1S5ZOOJsKcdGBV41KvRj2v3xndc"; // <-- Replace this
const LIBRARIES = ["places"];

const Traveler = () => {
  // Existing states
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [expandedRegions, setExpandedRegions] = useState({});

  // Search related states
  const [searchInput, setSearchInput] = useState("");
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [isSearchCountry, setIsSearchCountry] = useState(null);
  const [searchCityName, setSearchCityName] = useState("");
  const [searchCountryPlaces, setSearchCountryPlaces] = useState([]);
  const [searchPlaces, setSearchPlaces] = useState([]);
  const [searchShrines, setSearchShrines] = useState([]);
  const [searchRestaurants, setSearchRestaurants] = useState([]);

  // Handle continent-country selection
  const handleCountryClick = (continentName, countryName) => {
    setSelectedCountry({ continentName, countryName });
    setExpandedRegions({}); // reset expanded regions on country change
    // Clear search results on manual country select
    clearSearchResults();
  };

  const toggleRegion = (regionName) => {
    setExpandedRegions((prev) => ({
      ...prev,
      [regionName]: !prev[regionName],
    }));
  };

  const clearSearchResults = () => {
    setSearchInput("");
    setIsSearchCountry(null);
    setSearchCityName("");
    setSearchCountryPlaces([]);
    setSearchPlaces([]);
    setSearchShrines([]);
    setSearchRestaurants([]);
  };

  // Filter continents by selected country
  const filteredContinents = selectedCountry
    ? TravelData.filter((c) => c.continent === selectedCountry.continentName)
    : TravelData;

  // Helpers to check if geocode result is a country
  const checkIfCountry = (placeResults) => {
    if (!placeResults || placeResults.length === 0) return false;
    return placeResults[0].types.includes("country");
  };

  // Fetch tourist places in a country using Places API
  const fetchTouristPlacesInCountry = (countryName, map, service) => {
    setLoadingSearch(true);
    const request = {
      location: map.getCenter(),
      radius: 50000,
      keyword: "tourist attractions",
      type: "tourist_attraction",
    };
    service.nearbySearch(request, (results, status) => {
      if (status === window.google.maps.places.PlacesServiceStatus.OK) {
        setSearchCountryPlaces(results.slice(0, 10));
      } else {
        setSearchCountryPlaces([]);
      }
      setLoadingSearch(false);
    });
  };

  // Fetch detailed places by keywords for a city/place
  const fetchDetailData = (placeName, map, center) => {
    setLoadingSearch(true);
    const service = new window.google.maps.places.PlacesService(map);

    const search = (keyword, setter) => {
      const request = { location: center, radius: 20000, keyword };
      service.nearbySearch(request, (results, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK) {
          const filtered = results.filter(
            (item) => item.photos && item.photos.length > 0
          );
          setter(filtered.slice(0, 8));
        } else {
          setter([]);
        }
        setLoadingSearch(false);
      });
    };

    search(`tourist places in ${placeName}`, setSearchPlaces);
    search(`temples in ${placeName}`, setSearchShrines);
    search(`fine dining in ${placeName}`, setSearchRestaurants);
  };

  // Handle search submit
  const handleSearch = () => {
    if (!searchInput.trim()) return;

    setLoadingSearch(true);
    setSearchCityName("");
    setIsSearchCountry(null);
    setSearchCountryPlaces([]);
    setSearchPlaces([]);
    setSearchShrines([]);
    setSearchRestaurants([]);

    const map = new window.google.maps.Map(document.createElement("div"));
    const geocoder = new window.google.maps.Geocoder();

    geocoder.geocode({ address: searchInput.trim() }, (results, status) => {
      if (status === "OK" && results.length > 0) {
        const location = results[0].geometry.location;
        map.setCenter(location);

        const isCountryResult = checkIfCountry(results);
        setIsSearchCountry(isCountryResult);

        if (isCountryResult) {
          const service = new window.google.maps.places.PlacesService(map);
          fetchTouristPlacesInCountry(searchInput.trim(), map, service);
          setLoadingSearch(false);
        } else {
          setSearchCityName(searchInput.trim());
          fetchDetailData(searchInput.trim(), map, location);
        }
      } else {
        alert("Location not found. Please enter a valid country or place.");
        setLoadingSearch(false);
      }
    });
  };

  return (
    <>
      <AppNavbar />
      <LoadScript
        googleMapsApiKey={GOOGLE_API_KEY}
        libraries={LIBRARIES}
        language="en"
      >
        <div>
          {/* Top Navbar */}
          <Navbar expand="lg" bg="light" className="mb-4 border border-top">
            <Container fluid>
              <Navbar.Brand href="#" className="text-primary fw-bold">
                {" "}
                Travel Explorer
              </Navbar.Brand>
              <Navbar.Toggle aria-controls="travel-navbar" />
              <Navbar.Collapse id="travel-navbar">
                <Nav className="me-auto">
                  {TravelData.map((continent, ci) => (
                    <NavDropdown
                      title={continent.continent}
                      id={`continent-${ci}`}
                      key={ci}
                    >
                      {continent.countries.map((country, cIndex) => (
                        <NavDropdown.Item
                          key={cIndex}
                          onClick={() =>
                            handleCountryClick(
                              continent.continent,
                              country.name
                            )
                          }
                        >
                          {country.name}
                        </NavDropdown.Item>
                      ))}
                    </NavDropdown>
                  ))}
                </Nav>
                <Form
                  className="d-flex ms-auto"
                  style={{ maxWidth: "300px" }}
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSearch();
                  }}
                >
                  <FormControl
                    type="search"
                    placeholder="Search destinations..."
                    className="me-2"
                    aria-label="Search"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  />
                  <Button
                    variant="primary"
                    onClick={handleSearch}
                    disabled={loadingSearch}
                  >
                    Search
                  </Button>
                </Form>
              </Navbar.Collapse>
            </Container>
          </Navbar>

          {/* Header Section */}
          <Container>
            {/* Show existing filtered continents & countries if no search results */}
            {!searchCityName && !isSearchCountry && (
              <>
                <h4
                  style={{
                    fontSize: "4rem",
                    fontWeight: 700,
                    lineHeight: "0.9",
                    color: "#3a3e61",
                    letterSpacing: "-2px",
                    overflow: "hidden",
                  }}
                  className="text-center"
                >
                  Destination {selectedCountry?.countryName || "Bharat"}
                </h4>
                <h4 className="mb-4 text-center">
                  A potpourri of vibrant cultures, exotic destinations and
                  enduring memories; welcome to one of the heartlands of human
                  civilization.
                </h4>
                {filteredContinents.map((continent, ci) => (
                  <div key={ci}>
                    {continent.countries
                      .filter(
                        (country) =>
                          !selectedCountry ||
                          country.name === selectedCountry.countryName
                      )
                      .map((country, cIndex) => (
                        <div key={cIndex}>
                          {/* Bharat has regions as object */}
                          {country.name === "Bharat" ? (
                            Object.entries(country.regions).map(
                              ([regionName, places]) => {
                                const isExpanded = expandedRegions[regionName];
                                const visiblePlaces = isExpanded
                                  ? places
                                  : places.slice(0, 8);
                                return (
                                  <div key={regionName} className="mb-5">
                                    <h3 className="mb-3 fw-bold">
                                      {regionName}
                                    </h3>
                                    <Row>
                                      {visiblePlaces.map((place, index) => (
                                        <Col
                                          key={index}
                                          xs={12}
                                          sm={6}
                                          md={4}
                                          lg={3}
                                        >
                                          <NavLink
                                            to={`/city/${place.name}`}
                                            style={{
                                              textDecoration: "none",
                                              color: "inherit",
                                            }}
                                          >
                                            <div className="yacht-tile">
                                              <img
                                                src={place.image}
                                                alt={place.name}
                                                className="img-fluid rounded"
                                              />
                                              <div className="overlay text-center mt-2">
                                                <h5 className="fw-bold">
                                                  {place.name}
                                                </h5>
                                              </div>
                                            </div>
                                            <p className="text-muted small">
                                              {place.famousFor}
                                            </p>
                                          </NavLink>
                                        </Col>
                                      ))}
                                    </Row>
                                    {places.length > 8 && (
                                      <div className="text-end mt-3">
                                        <Button
                                          variant="dark"
                                          className="explore-more-btn btn-sm"
                                          onClick={() =>
                                            toggleRegion(regionName)
                                          }
                                        >
                                          {isExpanded
                                            ? "Show Less"
                                            : "Explore More"}
                                        </Button>
                                      </div>
                                    )}
                                  </div>
                                );
                              }
                            )
                          ) : country.regions?.Cities?.length ? (
                            (() => {
                              const regionName = "Cities";
                              const places = country.regions.Cities;
                              const isExpanded = expandedRegions[regionName];
                              const visiblePlaces = isExpanded
                                ? places
                                : places.slice(0, 8);
                              return (
                                <div key={regionName} className="mb-5">
                                  <h3 className="mb-3 fw-bold">{regionName}</h3>
                                  <Row>
                                    {visiblePlaces.map((place, index) => (
                                      <Col
                                        key={index}
                                        xs={12}
                                        sm={6}
                                        md={4}
                                        lg={3}
                                      >
                                        <NavLink
                                          to={`/city/${place.name}`}
                                          style={{
                                            textDecoration: "none",
                                            color: "inherit",
                                          }}
                                        >
                                          <div className="yacht-tile">
                                            <img
                                              src={place.image}
                                              alt={place.name}
                                              className="img-fluid rounded"
                                            />
                                            <div className="overlay text-center mt-2">
                                              <h5 className="fw-bold">
                                                {place.name}
                                              </h5>
                                            </div>
                                          </div>
                                          <p className="text-muted small">
                                            {place.famousFor}
                                          </p>
                                        </NavLink>
                                      </Col>
                                    ))}
                                  </Row>
                                  {places.length > 8 && (
                                    <div className="text-end mt-3">
                                      <Button
                                        variant="dark"
                                        className="explore-more-btn btn-sm"
                                        onClick={() => toggleRegion(regionName)}
                                      >
                                        {isExpanded
                                          ? "Show Less"
                                          : "Explore More"}
                                      </Button>
                                    </div>
                                  )}
                                </div>
                              );
                            })()
                          ) : (
                            <p className="text-muted">
                              No destinations available for this country
                            </p>
                          )}
                        </div>
                      ))}
                  </div>
                ))}
              </>
            )}

            {/* SEARCH RESULTS SECTION */}
            {loadingSearch && (
              <div className="text-center py-5">
                <Spinner animation="border" />
                <p>Loading places...</p>
              </div>
            )}

            {isSearchCountry && searchCountryPlaces.length > 0 && (
              <Container className="mb-5">
                <h3 className="mb-3 fw-bold">
                  Top Tourist Places in {searchInput.trim()}
                </h3>
                <Row>
                  {searchCountryPlaces.map((place, index) => (
                    <Col
                      key={index}
                      xs={12}
                      sm={6}
                      md={4}
                      lg={3}
                      className="mb-4"
                    >
                      <NavLink
                        to={`/city/${place.name}`}
                        style={{
                          textDecoration: "none",
                          color: "inherit",
                        }}
                      >
                        <div className="yacht-tile">
                          <img
                            src={place.image}
                            alt={place.name}
                            className="img-fluid rounded"
                          />
                          <div className="overlay text-center mt-2">
                            <h5 className="fw-bold">{place.name}</h5>
                          </div>
                        </div>
                        <p className="text-muted small">{place.famousFor}</p>
                      </NavLink>
                    </Col>
                  ))}
                </Row>
              </Container>
            )}

            {!isSearchCountry && searchCityName && (
              <Container className="mb-5">
                <h4 className="fw-bold mb-3">
                  Discover {searchCityName}'s Local Attractions, Holy Shrines,
                  and Fine Dining Restaurants
                </h4>

                {/* Local Attractions */}
                {searchPlaces.length > 0 && (
                  <>
                    <h5>Local Attractions</h5>
                    <Row>
                      {searchPlaces.map((item, idx) => (
                        <Col
                          key={idx}
                          xs={12}
                          sm={6}
                          md={4}
                          lg={3}
                          className="mb-4"
                        >
                          <a
                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                              item.name
                            )}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ textDecoration: "none", color: "inherit" }}
                          >
                            <div className="yacht-tile">
                              <img
                                src={item.photos?.[0]?.getUrl() || ""}
                                alt={item.name}
                                className="img-fluid rounded"
                              />
                              <div className="overlay text-center mt-2">
                                <h5 className="fw-bold">{item.name}</h5>
                              </div>
                            </div>
                          </a>
                          <p className="text-muted small">
                            {item.vicinity || item.formatted_address}
                          </p>
                        </Col>
                      ))}
                    </Row>
                  </>
                )}

                {/* Holy Shrines */}
                {searchShrines.length > 0 && (
                  <>
                    <h5>Holy Shrines</h5>
                    <Row>
                      {searchShrines.map((item, idx) => (
                        <Col
                          key={idx}
                          xs={12}
                          sm={6}
                          md={4}
                          lg={3}
                          className="mb-4"
                        >
                          <a
                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                              item.name
                            )}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ textDecoration: "none", color: "inherit" }}
                          >
                            <div className="yacht-tile">
                              <img
                                src={item.photos?.[0]?.getUrl() || ""}
                                alt={item.name}
                                className="img-fluid rounded"
                              />
                              <div className="overlay text-center mt-2">
                                <h5 className="fw-bold">{item.name}</h5>
                              </div>
                            </div>
                          </a>
                          <p className="text-muted small">
                            {item.vicinity || item.formatted_address}
                          </p>
                        </Col>
                      ))}
                    </Row>
                  </>
                )}

                {/* Fine Dining */}
                {searchRestaurants.length > 0 && (
                  <>
                    <h5>Fine Dining Restaurants</h5>
                    <Row>
                      {searchRestaurants.map((item, idx) => (
                        <Col
                          key={idx}
                          xs={12}
                          sm={6}
                          md={4}
                          lg={3}
                          className="mb-4"
                        >
                          <a
                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                              item.name
                            )}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ textDecoration: "none", color: "inherit" }}
                          >
                            <div className="yacht-tile">
                              <img
                                src={item.photos?.[0]?.getUrl() || ""}
                                alt={item.name}
                                className="img-fluid rounded"
                              />
                              <div className="overlay text-center mt-2">
                                <h5 className="fw-bold">{item.name}</h5>
                              </div>
                            </div>
                          </a>
                          <p className="text-muted small">
                            {item.vicinity || item.formatted_address}
                          </p>
                        </Col>
                      ))}
                    </Row>
                  </>
                )}
              </Container>
            )}
          </Container>
        </div>
      </LoadScript>
    </>
  );
};

export default Traveler;
